import React, { useEffect, useState } from "react";
import Col from "react-bootstrap/Col";
import Modal from "react-bootstrap/Modal";
import Row from "react-bootstrap/Row";
import DataTable from "react-data-table-component";
import ItemService from "../../../../services/customer_appCommonService";

const EstimateMaterialSelectionModal = React.forwardRef((props, ref) => {

  const [disabled, setDisabled] = useState(true);
  const [data, setData] = useState([]);
  const [fullData, setFullData] = useState([]);
  const [getSelectedData, setSelectedData] = useState([]);
  const [searchCode, setSearchCode] = useState("");

  const [filterType, setFilterType] = useState("");
  const [selectedValue, setSelectedValue] = useState("");
  const [subGroupValue, setSubGroupValue] = useState("");

  // 🔹 Dropdown Maps
  const [groupToCodesMap, setGroupToCodesMap] = useState({});
  const [categoryToSubCategoryMap, setCategoryToSubCategoryMap] = useState({});
  const [codeToDescMap, setCodeToDescMap] = useState({});

  const filterTypeOptions = [
    { label: "Material Group", value: "materialGroup" },
    { label: "Material Group Code", value: "materialGroupCode" },
    { label: "Material Category Code", value: "materialCategoryCode" },
  ];

  const materialGroupOptions = Object.keys(groupToCodesMap);
  const materialGroupCodeOptions = [
    ...new Set(Object.values(groupToCodesMap).flat()),
  ];

  const subGroupOptions =
    filterType === "materialGroup" && selectedValue
      ? groupToCodesMap[selectedValue] || []
      : filterType === "materialCategoryCode" && selectedValue
      ? categoryToSubCategoryMap[selectedValue] || []
      : [];
//eslint-disable-next-line
  const subFilterKeyMap = {
    materialGroup: "materialGroupCode",
    materialCategoryCode: "materialSubCategoryCode",
  };

  // 🔹 Direct API call
  useEffect(() => {
    const fetchAllMaterials = async () => {
      try {
        const obj = {
          queryName: "Get_MATERIAL_LIST",
          page: 1,
          per_page: 999999,
        };
        const res = await ItemService.generateCustomerQuery(obj);
        const allData = res?.data || [];

        // ── Table data ──────────────────────────────────────────────
        const mappedData = allData.map((item) => ({
          code: item.materialCode || "--",
          description: item.materialDescription || "--",
          avg: item.avg !== 0 ? item.avg : 1,
          quantity: item.quntity || 0,
          pieceMeasure: item.pieceMeasure || 0,
          unit: item.unit || "--",
          unitWeight: item.unitWeight || 0,
          unitPrice: item.unitPrice || 0,
          pieceWeight: item.weightPice || 0,
          totalWeight: item.weightTotal || 0,
          price: item.bookPrice || 0,
          totalPrice: item.bookTotalPrice || 0,
          unitWeightModifier: item.unitWeightModifier || 1,
          unitPriceModifier: item.unitPriceModifier || 1,
          remarks: item.remarks || "--",
          materialGroup: item.materialGroup || "--",
          materialGroupCode: item.materialGroupCode || "--",
          materialCategoryCode: item.materialCategoryCode || "--",
          materialSubCategoryCode: item.materialSubCategoryCode || "--",
          materialImage: item.materialImage || "",
          surfacePaintArea: item.surfacePaintArea || "",
        }));
        setData(mappedData);
        setFullData(mappedData);

        // ── Group → GroupCodes + CategoryCodes + SubCategoryCodes ───
        const map = {};
        allData.forEach((item) => {
          const group = item.materialGroup;
          const code = item.materialGroupCode;
          const catCode = item.materialCategoryCode;
          const subCatCode = item.materialSubCategoryCode;
          if (group) {
            if (!map[group]) map[group] = new Set();
            if (code) map[group].add(code);
            if (catCode) map[group].add(catCode);
            if (subCatCode) map[group].add(subCatCode);
          }
        });
        const finalMap = {};
        Object.keys(map).forEach((key) => {
          finalMap[key] = Array.from(map[key]);
        });
        setGroupToCodesMap(finalMap);

        // ── Category → SubCategory ───────────────────────────────────
        const catMap = {};
        allData.forEach((item) => {
          const cat = item.materialCategoryCode;
          const sub = item.materialSubCategoryCode;
          if (cat) {
            if (!catMap[cat]) catMap[cat] = new Set();
            if (sub) catMap[cat].add(sub);
          }
        });
        const finalCatMap = {};
        Object.keys(catMap).forEach((key) => {
          finalCatMap[key] = Array.from(catMap[key]);
        });
        setCategoryToSubCategoryMap(finalCatMap);

        // ── Code → Description ───────────────────────────────────────
        const descMap = {};
        allData.forEach((item) => {
          if (item.materialGroupCode)
            descMap[item.materialGroupCode] = item.materialGroupDesc || "";
          if (item.materialCategoryCode)
            descMap[item.materialCategoryCode] = item.materialCategoryDesc || "";
          if (item.materialSubCategoryCode)
            descMap[item.materialSubCategoryCode] =
              item.materialSubCategoryDesc || "";
        });
        setCodeToDescMap(descMap);

      } catch (err) {
        console.error("Failed to fetch materials", err);
      }
    };

    fetchAllMaterials();
  }, []);

  // 🔹 Table Row Selection
  const handleChangeCat = ({ selectedRows }) => {
    setSelectedData(selectedRows);
    setDisabled(selectedRows.length === 0);
  };

  // 🔹 Submit Selected
  const handleSubmit = () => {
    props.onselectedcatdata(getSelectedData);
    props.onHide();
  };

  // 🔹 Filter — client-side
  const handleFilter = () => {
    if (!filterType || !selectedValue) {
      alert("Please select a filter type and value");
      return;
    }

    const filtered = fullData.filter((row) => {
      if (filterType === "materialGroup" && subGroupValue) {
        return (
          row.materialGroupCode === subGroupValue ||
          row.materialCategoryCode === subGroupValue ||
          row.materialSubCategoryCode === subGroupValue
        );
      }
      if (filterType === "materialGroup")
        return row.materialGroup === selectedValue;
      if (filterType === "materialGroupCode")
        return row.materialGroupCode === selectedValue;
      if (filterType === "materialCategoryCode" && subGroupValue)
        return row.materialSubCategoryCode === subGroupValue;
      if (filterType === "materialCategoryCode")
        return row.materialCategoryCode === selectedValue;
      return true;
    });
    setData(filtered);
  };

  // 🔹 Reset — fullData తో reset
  const handleReset = () => {
    setFilterType("");
    setSelectedValue("");
    setSubGroupValue("");
    setSearchCode("");
    setData(fullData);
  };

  // 🔹 Search by Code — client-side
  const handleSearchByCode = () => {
    if (!searchCode.trim()) {
      alert("Please enter a material code");
      return;
    }
    const filtered = fullData.filter((row) =>
      row.code.toLowerCase().includes(searchCode.trim().toLowerCase())
    );
    setData(filtered);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter") handleSearchByCode();
  };

  // 🔹 Table Columns
  const columns = [
    {
      name: "Code & Description",
      sortable: true,
      grow: 2,
      cell: (row) => (
        <div>
          <div className="fw-bold">{row.code}</div>
          <div>{row.description}</div>
        </div>
      ),
    },
    { name: "UOM", selector: (row) => row.unit, sortable: true },
    {
      name: "Unit Weight",
      selector: (row) =>
        typeof row.unitWeight === "number"
          ? row.unitWeight.toFixed(4)
          : row.unitWeight,
      sortable: true,
    },
    { name: "Unit Price", selector: (row) => row.unitPrice, sortable: true },
    {
      name: "Group",
      selector: (row) => row.materialGroup,
      cell: (row) => (
        <div>
          <div className="fw-bold">{row.materialGroup}</div>
          <div>{row.materialGroupCode}</div>
        </div>
      ),
      sortable: true,
    },
    {
      name: "Category",
      selector: (row) => row.materialCategoryCode,
      cell: (row) => (
        <div>
          <div className="fw-bold">{row.materialCategoryCode}</div>
          <div>{row.materialSubCategoryCode}</div>
        </div>
      ),
      sortable: true,
    },
    {
      name: "Surface/Paint",
      selector: (row) => row.surfacePaintArea,
      sortable: true,
    },
  ];

  return (
    <Modal
      {...props}
      aria-labelledby="contained-modal-title-vcenter"
      keyboard={false}
      dialogClassName="modal-90w"
      ref={ref}
      centered
      backdrop="static"
    >
      <Modal.Header closeButton>
        <Modal.Title>Material Selection</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Row className="mb-3 align-items-end g-2">

          {/* Filter Type */}
          <Col xs={12} md={2} className="commTopButtonRightLeftPadding">
            <label>Filter Type</label>
            <select
              className="form-select"
              value={filterType}
              onChange={(e) => {
                setFilterType(e.target.value);
                setSelectedValue("");
                setSubGroupValue("");
              }}
            >
              <option value="">-- Select Filter Type --</option>
              {filterTypeOptions.map((f) => (
                <option key={f.value} value={f.value}>{f.label}</option>
              ))}
            </select>
          </Col>

          {/* Main Value Dropdown */}
          {filterType && (
            <Col xs={12} md={2} className="commTopButtonRightLeftPadding">
              <label>
                {filterTypeOptions.find((f) => f.value === filterType)?.label}
              </label>
              <select
                className="form-select"
                value={selectedValue}
                onChange={(e) => {
                  setSelectedValue(e.target.value);
                  setSubGroupValue("");
                }}
              >
                <option value="">-- Select --</option>
                {filterType === "materialGroup" &&
                  materialGroupOptions.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                {filterType === "materialGroupCode" &&
                  materialGroupCodeOptions.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                {filterType === "materialCategoryCode" &&
                  Object.keys(categoryToSubCategoryMap).map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
              </select>
            </Col>
          )}

          {/* Sub-Group / Sub-Category Dropdown */}
          {(filterType === "materialGroup" ||
            filterType === "materialCategoryCode") &&
            selectedValue &&
            subGroupOptions.length > 0 && (
            <Col xs={12} md={2} className="commTopButtonRightLeftPadding">
              <label>
                {filterType === "materialCategoryCode"
                  ? "Sub Category Code"
                  : "Sub Group (Code)"}
              </label>
              <select
                className="form-select"
                value={subGroupValue}
                onChange={(e) => setSubGroupValue(e.target.value)}
              >
                <option value="">
                  -- All in {filterType === "materialCategoryCode" ? "Category" : "Group"} --
                </option>
                {subGroupOptions.map((opt) => (
                  <option key={opt} value={opt}>
                    {`${opt}${codeToDescMap[opt] ? ` - ${codeToDescMap[opt]}` : ""}`}
                  </option>
                ))}
              </select>
            </Col>
          )}

          {/* Search by Material Code */}
          <Col xs={12} md={2} className="commTopButtonRightLeftPadding">
            <label>Search by Material Code & Press Enter ↵</label>
            <input
              type="text"
              className="form-control"
              placeholder="Enter material code..."
              value={searchCode}
              onChange={(e) => setSearchCode(e.target.value)}
              onKeyPress={handleSearchKeyPress}
            />
          </Col>

          {/* Filter + Reset Buttons */}
          <Col xs={12} md={2} className="commTopButtonRightLeftPadding d-flex flex-row gap-2 align-items-end">
            <button
              type="button"
              className="btn btn-primary flex-fill"
              onClick={handleFilter}
            >
              Filter
            </button>
            <button
              type="button"
              className="btn btn-secondary flex-fill btn-danger"
              onClick={handleReset}
            >
              Reset
            </button>
          </Col>

        </Row>

        {/* Data Table */}
        <DataTable
          columns={columns}
          data={data}
          selectableRows
          onSelectedRowsChange={handleChangeCat}
          pagination
          highlightOnHover
          striped
          dense
          responsive
        />

        {/* Action Buttons */}
        <Row className="justify-end mt-4">
          <Col xs={6} md={2}>
            <button
              type="button"
              onClick={() => props.onHide()}
              className="btn btn-danger w-100"
            >
              Cancel
            </button>
          </Col>
          <Col xs={6} md={2}>
            <button
              type="button"
              onClick={handleSubmit}
              className="btn btn-primary w-100 mx-1"
              disabled={disabled}
            >
              Select
            </button>
          </Col>
        </Row>
      </Modal.Body>
    </Modal>
  );
});

export default EstimateMaterialSelectionModal;